import { ExperimentTemplate } from '@experiment-details/types';
import { debounce, get, isEqual, set } from 'lodash';
import { useEffect, useState } from 'react';
import useUpdateSelectedTemplate from './template/useUpdateSelectedTemplate';
import { Paths } from '@shared/types';
import useExpDetailsSelectedTemplate from './useExpDetailsSelectedTemplate';

export type TemplateUpdateAction =
  | 'none'
  | 'remove-panel'
  | 'remove-section'
  | 'add-panel'
  | 'edit-panel';

const freezingActionTypes: TemplateUpdateAction[] = [
  'remove-panel',
  'remove-section'
];

const saveImmediatelyActionTypes: TemplateUpdateAction[] = [
  'remove-panel',
  'remove-section',
  'add-panel',
  'edit-panel'
];

function useExperimentDetailsTemplateState<T>(
  field: Paths<ExperimentTemplate>,
  defaultValue: T | null = null
): [T, (newValue: T) => void] {
  const { templateData: currentTemplate } = useExpDetailsSelectedTemplate();
  const templateValue = get(
    currentTemplate?.chartTemplate,
    field,
    defaultValue
  ) as T;
  const updateSelectedTemplate = useUpdateSelectedTemplate();
  const debouncedUpdateSelectedTemplate = debounce(updateSelectedTemplate, 300);

  const [value, setValue] = useState<T>(templateValue);

  // Sync value when new template is selected
  useEffect(() => {
    if (!isEqual(templateValue, value)) {
      setValue(templateValue);
    }
  }, [currentTemplate?.templateId, templateValue]);

  const updateTemplate = (
    newValue: T,
    actionType: TemplateUpdateAction = 'none'
  ) => {
    if (newValue === templateValue) return;

    const update = set({}, field, newValue);

    if (freezingActionTypes.includes(actionType)) {
      set(update, 'panels.autoGenerate', false);
    }

    if (saveImmediatelyActionTypes.includes(actionType)) {
      updateSelectedTemplate(update);
      return;
    }

    debouncedUpdateSelectedTemplate(update);
  };

  const onFieldUpdate = (newValue: T, actionType?: TemplateUpdateAction) => {
    setValue(newValue);
    updateTemplate(newValue, actionType);
  };

  return [value, onFieldUpdate];
}

export default useExperimentDetailsTemplateState;
